import uuid from 'uuid/v4';

import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import type { CollabEventConflictPayload } from '@atlaskit/editor-common/collab';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { EVENT_TYPE, ACTION_SUBJECT } from '@atlaskit/editor-common/analytics';

import type { ConflictResolutionUIPlugin } from '../conflictResolutionUIPluginType';

import { conflictResolutionPluginKey } from './pluginKey';
import type { Conflicts, IdentifiableConflictChange, ConflictChangeWithType } from './main';
import { attachAnalyticsEvent } from './analytics';

type Direction = 'next' | 'previous';

type NavigationMetadata = {
	type: 'navigate';
	direction: Direction;
	data: IdentifiableConflictChange | undefined;
};

export type Metadata =
	| {
			type: 'update';
			data: CollabEventConflictPayload;
	  }
	| {
			type: 'resolve';
			data: IdentifiableConflictChange;
	  }
	| {
			type: 'resolveAll';
	  }
	| NavigationMetadata
	| {
			type: 'restore';
			data: IdentifiableConflictChange;
	  }
	| {
			type: 'startResolving';
	  }
	| {
			type: 'stopResolving';
	  };

export const getNewConflicts = (
	metadata: Metadata | undefined,
	existingConflicts: Conflicts | undefined,
): Conflicts | undefined => {
	if (!metadata) {
		return undefined;
	}
	switch (metadata.type) {
		// Navigate between conflicts using arrows - doesn't modify conflicts
		case 'navigate':
		// Enter/exit conflict resolution mode - doesn't modify conflicts
		case 'startResolving':
		case 'stopResolving':
			return existingConflicts;
		// Receive new conflicts from server after reconnection
		case 'update':
			return {
				inserted: metadata.data.inserted.map((c) => ({ ...c, id: uuid() })),
				deleted: metadata.data.deleted.map((c) => ({ ...c, id: uuid() })),
			};
		// User accepts/dismisses a single conflict (removes it from the list)
		case 'resolve':
		// User chooses to keep deleted text (restores it, then removes conflict from the list)
		case 'restore':
			if (!existingConflicts) {
				return existingConflicts;
			}
			return {
				inserted: existingConflicts.inserted.filter((conflict) => conflict.id !== metadata.data.id),
				deleted: existingConflicts.deleted.filter((conflict) => conflict.id !== metadata.data.id),
			};
		// User accepts all conflicts at once (clears the entire list)
		case 'resolveAll':
			return {
				inserted: [],
				deleted: [],
			};
		default:
			return undefined;
	}
};

export const getNavigationInfo = (
	conflicts: Conflicts,
	selected: IdentifiableConflictChange | undefined,
	direction: Direction,
): { isAbleToNavigate: boolean; newConflict: IdentifiableConflictChange | undefined } => {
	const allConflicts = [...(conflicts?.inserted ?? []), ...(conflicts?.deleted ?? [])].sort(
		(a, b) => a.from - b.from,
	);
	if (!selected) {
		return {
			isAbleToNavigate: true,
			newConflict: allConflicts.length > 0 ? allConflicts[0] : undefined,
		};
	}
	const idx = allConflicts.findIndex((c) => c.id === selected.id);
	if (direction === 'next') {
		const isAbleToNavigate = idx < allConflicts.length - 1;
		return { isAbleToNavigate, newConflict: isAbleToNavigate ? allConflicts[idx + 1] : undefined };
	} else {
		const isAbleToNavigate = idx > 0;
		return { isAbleToNavigate, newConflict: isAbleToNavigate ? allConflicts[idx - 1] : undefined };
	}
};

const addTypeToConflict = (
	conflict: IdentifiableConflictChange,
	conflicts: Conflicts,
): ConflictChangeWithType => {
	const type: ConflictChangeWithType['type'] = conflicts.deleted.find((c) => c.id === conflict.id)
		? 'deleted'
		: 'inserted';
	return { ...conflict, type };
};

const navigateToSelection = (meta: NavigationMetadata, tr: Transaction, conflicts: Conflicts) => {
	const { isAbleToNavigate, newConflict } = getNavigationInfo(conflicts, meta.data, meta.direction);
	if (isAbleToNavigate && newConflict) {
		return tr
			.setSelection(TextSelection.create(tr.doc, newConflict.from, newConflict.to))
			.setMeta('nextConflict', addTypeToConflict(newConflict, conflicts))
			.scrollIntoView();
	} else {
		return null;
	}
};

// Try to to navigate to the "next" available, but if not try the previous selection
export const navigateToAvailable = (
	currentConflict: IdentifiableConflictChange | undefined,
	tr: Transaction,
	conflicts: Conflicts,
) => {
	return (
		navigateToSelection(
			{ type: 'navigate', direction: 'next', data: currentConflict },
			tr,
			conflicts,
		) ??
		navigateToSelection(
			{ type: 'navigate', direction: 'previous', data: currentConflict },
			tr,
			conflicts,
		)
	);
};

type Options = {
	api: ExtractInjectionAPI<ConflictResolutionUIPlugin> | undefined;
};

export const setMetadata = (editorView: EditorView, meta: Metadata, options?: Options) => {
	const {
		dispatch,
		state: { tr },
	} = editorView;
	if (meta.type === 'navigate') {
		const pluginState = conflictResolutionPluginKey.getState(editorView.state);
		if (pluginState?.conflicts) {
			navigateToSelection(meta, tr, pluginState?.conflicts);
		}
	} else if (meta.type === 'restore') {
		tr.replace(meta.data.from, meta.data.to, meta.data.local);
	} else if (meta.type === 'update') {
		attachAnalyticsEvent(options?.api?.analytics?.actions)({
			actionSubject: ACTION_SUBJECT.EDITOR,
			attributes: {
				inserted: meta.data.inserted.length,
				deleted: meta.data.deleted.length,
			},
			eventType: EVENT_TYPE.TRACK,
			action: 'offlineReconnectionConflict',
		})?.(tr);
	} else if (meta.type === 'resolve') {
		const pluginState = conflictResolutionPluginKey.getState(editorView.state);
		if (pluginState?.conflicts) {
			navigateToAvailable(meta.data, tr, pluginState?.conflicts);
		}
	}
	dispatch(tr.setMeta(conflictResolutionPluginKey, meta));
};
